import React, { useState } from 'react'

import type { InputProps } from '@fourthwall/components'
import { Button, ButtonClean, Icon, Input, Loader, Subsection } from '@fourthwall/components'
import { PreviewOffIcon, PreviewOnIcon } from '@fourthwall/icons'
import { routing } from '@utils/routing'

import * as S from './OpenAPI.styled'
import { CopyButton } from './components/CopyButton/CopyButton'
import { Warning } from './components/Warning/Warning'

export type OpenAPIViewProps = {
  user?: {
    username: string
    password: string
  }
  isLoading: boolean
  onUserCreate?: () => void
  onUserRemove: () => void
}

export const OpenAPIView = ({ user, isLoading, onUserCreate, onUserRemove }: OpenAPIViewProps) => {
  const [passwordInputType, setPasswordInputType] = useState<InputProps['type']>('password')

  const togglePasswordInputType = () => {
    setPasswordInputType((currentPasswordType) =>
      currentPasswordType === 'password' ? 'text' : 'password',
    )
  }

  return (
    <Subsection title="Open API" id="open-api">
      <S.Description>
        Authorize through Basic auth to integrate with Fourthwall’s API.{' '}
        <ButtonClean label="Learn more" href={routing.external.docs.openAPI} target="_blank" />
      </S.Description>

      {!user && onUserCreate && (
        <S.ButtonContainer>
          <Button label="Create API user" size="small" loading={isLoading} onClick={onUserCreate} />
        </S.ButtonContainer>
      )}

      {user && <Warning />}

      {user && (
        <>
          <S.InputContainer>
            <Input
              label="Username"
              value={user.username}
              append={
                <S.AppendContainer>
                  <CopyButton label="Copy" copyValue={user.username} size="small" />
                </S.AppendContainer>
              }
              readOnly
            />
          </S.InputContainer>
          <S.InputContainer>
            <Input
              label="Password"
              value={user.password}
              type={passwordInputType}
              append={
                <S.AppendContainer>
                  <S.AppendIcon onClick={togglePasswordInputType}>
                    <Icon
                      component={passwordInputType === 'password' ? PreviewOnIcon : PreviewOffIcon}
                      height={16}
                    />
                  </S.AppendIcon>
                  <CopyButton label="Copy" copyValue={user.password} size="small" />
                </S.AppendContainer>
              }
              readOnly
            />
          </S.InputContainer>
          <S.ButtonContainer>
            <ButtonClean label="Remove API user" disabled={isLoading} onClick={onUserRemove} />
            {isLoading && (
              <S.LoaderContainer>
                <Loader size="small" />
              </S.LoaderContainer>
            )}
          </S.ButtonContainer>
        </>
      )}
    </Subsection>
  )
}
